import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { adminApi, type AdminReportDetail } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Save, Building2, FileText, Calendar, Lock, Unlock } from "lucide-react";

interface AdminReportDetailModalProps {
  reportId: string;
  onClose: () => void;
  onUpdated: () => void;
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Utkast" },
  { value: "in_progress", label: "Pågående" },
  { value: "completed", label: "Klar" },
  { value: "exported", label: "Exporterad" },
  { value: "finalized", label: "Skickad till Bolagsverket" },
];

const statusBadgeStyles: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  exported: "bg-purple-100 text-purple-700",
  finalized: "bg-primary-100 text-primary-700",
};

function formatFiscalYear(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  if (s.getMonth() === 0 && e.getMonth() === 11) {
    return `${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString("sv-SE")} – ${e.toLocaleDateString("sv-SE")}`;
}

function formatDateTimeLocal(isoString: string | null | undefined): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  // Format as YYYY-MM-DDTHH:MM for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function parseLocalToIso(localString: string): string | null {
  if (!localString) return null;
  return new Date(localString).toISOString();
}

export function AdminReportDetailModal({ reportId, onClose, onUpdated }: AdminReportDetailModalProps) {
  const [reportDetail, setReportDetail] = useState<AdminReportDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Edit form state
  const [status, setStatus] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [exportedAt, setExportedAt] = useState("");
  const [finalizedAt, setFinalizedAt] = useState("");

  useEffect(() => {
    loadReport();
  }, [reportId]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getReport(reportId);
      if (response.success && response.data) {
        const r = response.data;
        setReportDetail(r);
        setStatus(r.status);
        setCompletedAt(formatDateTimeLocal(r.completedAt));
        setExportedAt(formatDateTimeLocal(r.exportedAt));
        setFinalizedAt(formatDateTimeLocal(r.finalizedAt));
      } else {
        setError(response.error?.message || "Kunde inte ladda årsredovisning");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.updateReport(reportId, {
        status,
        completedAt: parseLocalToIso(completedAt),
        exportedAt: parseLocalToIso(exportedAt),
        finalizedAt: parseLocalToIso(finalizedAt),
      });
      if (response.success) {
        onUpdated();
        toast.success("Årsredovisningen har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte uppdatera årsredovisning");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  // Quickly unlock the report (clear all lock timestamps and set status back to draft/in_progress)
  const handleUnlock = () => {
    setStatus("in_progress");
    setCompletedAt("");
    setExportedAt("");
    setFinalizedAt("");
  };

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  const isLocked = reportDetail?.status === "finalized" || reportDetail?.status === "completed";

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText size={20} className="text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Redigera årsredovisning</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error && !reportDetail ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : reportDetail ? (
            <div className="space-y-5">
              {/* Company Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <Building2 size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{reportDetail.companyName}</p>
                    <p className="text-sm text-gray-500">{reportDetail.organizationNumber}</p>
                  </div>
                </div>
              </div>

              {/* Fiscal Year */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Calendar size={18} className="text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Räkenskapsår</p>
                  <p className="text-sm text-blue-700">
                    {formatFiscalYear(reportDetail.fiscalYearStart, reportDetail.fiscalYearEnd)}
                  </p>
                </div>
              </div>

              {/* Quick Unlock Button */}
              {isLocked && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Lock size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Rapporten är låst</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Rapporten är markerad som slutförd eller skickad till Bolagsverket.
                        Du kan låsa upp den för att tillåta redigering.
                      </p>
                      <button
                        type="button"
                        onClick={handleUnlock}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
                      >
                        <Unlock size={14} />
                        Lås upp rapport
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Nuvarande status:{" "}
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${statusBadgeStyles[reportDetail.status] || "bg-gray-100 text-gray-700"}`}>
                    {STATUS_OPTIONS.find((o) => o.value === reportDetail.status)?.label || reportDetail.status}
                  </span>
                </p>
              </div>

              {/* Timestamps */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Calendar size={14} />
                  Tidsstämplar
                </h3>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Slutförd (completedAt)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={completedAt}
                      onChange={(e) => setCompletedAt(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {completedAt && (
                      <button
                        type="button"
                        onClick={() => setCompletedAt("")}
                        className="px-2 py-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
                        title="Rensa"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    När användaren markerade rapporten som slutförd. Styr redigeringsfönstrets countdown.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Exporterad (exportedAt)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={exportedAt}
                      onChange={(e) => setExportedAt(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {exportedAt && (
                      <button
                        type="button"
                        onClick={() => setExportedAt("")}
                        className="px-2 py-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
                        title="Rensa"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    När rapporten exporterades till PDF/iXBRL.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Skickad till Bolagsverket (finalizedAt)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={finalizedAt}
                      onChange={(e) => setFinalizedAt(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {finalizedAt && (
                      <button
                        type="button"
                        onClick={() => setFinalizedAt("")}
                        className="px-2 py-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
                        title="Rensa"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    När rapporten skickades till Bolagsverket. Sätts endast vid finalisering.
                  </p>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {reportDetail.id}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Steg:</span> {reportDetail.currentStep}/5
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Skapad:</span>{" "}
                  {new Date(reportDetail.createdAt).toLocaleString("sv-SE")}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Uppdaterad:</span>{" "}
                  {new Date(reportDetail.updatedAt).toLocaleString("sv-SE")}
                </p>
                {reportDetail.deletedAt && (
                  <p className="text-xs text-red-500">
                    <span className="font-medium">Borttagen:</span>{" "}
                    {new Date(reportDetail.deletedAt).toLocaleString("sv-SE")}
                  </p>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {reportDetail && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
              Spara ändringar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
